// Imports => MOBX
import {
  runInAction,
  makeObservable,
  extendObservable,
  observable,
  computed,
  action,
  set
} from 'mobx';

// Imports => Constants
import { TITLES } from '@constants';
import { KEYS } from '@opp/ui-kit';

// Imports => Utilities
import { AcUUID, AcIsSet, AcIsUndefined } from '@opp/ui-kit';

const _default = {
  modal: {
    id: AcUUID(),
    name: '',
    title: '',
    body: null,
    tag: null,
    callback: () => {},
    offset: false,
    visible: false,
    closeable: true,
    centered: false,
  },
  confirm_modal: {
    id: AcUUID(),
    visible: false,
    content: '',
  },
  navigation: {
    visible: true,
    expanded: [],
  },
  backdrop: {
    visible: false,
  },
  message_center: {
    visible: false,
  },
  support_center: {
    visible: false,
  },
  sub_navigation: {
    visible: false,
  },
  contextual_menu: {
    visible: false,
    id: null,
  },
  table_contextual_menu: {
    visible: false,
    id: null,
  },
  search: {
    focused: false,
  },
  drawer: {
    id: AcUUID(),
    visible: false,
    children: null,
  },
  breadcrumbs: {
    items: [],
  },
  new_window: null,
};

let _delay = null;
let _target = null;

export class UiStore {
  constructor(store) {
    makeObservable(this);

    this.store = store;
  }

    @observable
      navigation = JSON.parse(JSON.stringify(_default.navigation));

    @observable
      backdrop = JSON.parse(JSON.stringify(_default.backdrop));

    @observable
      message_center = JSON.parse(JSON.stringify(_default.message_center));

    @observable
      support_center = JSON.parse(JSON.stringify(_default.support_center));

    @observable
      sub_navigation = JSON.parse(JSON.stringify(_default.sub_navigation));

    @observable
      search = JSON.parse(JSON.stringify(_default.search));

    @observable
      contextual_menu = JSON.parse(JSON.stringify(_default.contextual_menu));

    @observable
      table_contextual_menu = JSON.parse(
        JSON.stringify(_default.table_contextual_menu)
      );

    @observable
      drawer = JSON.parse(JSON.stringify(_default.drawer));

    @observable
      breadcrumbs = JSON.parse(JSON.stringify(_default.breadcrumbs));

    @observable
      modal = JSON.parse(JSON.stringify(_default.modal));

    @observable
      confirm_modal = JSON.parse(JSON.stringify(_default.confirm_modal));

    @observable
      new_window = JSON.parse(JSON.stringify(_default.new_window));

    @computed
    get current_modal() {
      return this.modal;
    }

    @computed
    get current_confirm_modal() {
      return this.confirm_modal;
    }

    @computed
    get current_new_window() {
      return this.new_window;
    }

    @computed
    get current_navigation() {
      return this.navigation;
    }

    @computed
    get current_backdrop() {
      return this.backdrop;
    }
    @computed
    get current_message_center() {
      return this.message_center;
    }
    @computed
    get current_support_center() {
      return this.support_center;
    }
    @computed
    get current_sub_navigation() {
      return this.sub_navigation;
    }

    @computed
    get current_search() {
      return this.search;
    }

    @computed
    get current_contextual_menu() {
      return this.contextual_menu;
    }

    @computed
    get current_table_contextual_menu() {
      return this.table_contextual_menu;
    }

    @computed
    get current_drawer() {
      return this.drawer;
    }

    @computed
    get current_breadcrumbs() {
      return this.breadcrumbs && this.breadcrumbs.items;
    }

    @action
      multi_select = async (options = {}) => {
        await this.reset(KEYS.MODAL);
        return await this.set(KEYS.MODAL, {
          id: AcUUID(),
          title: TITLES.MULTI_SELECT,
          tag: KEYS.MULTI_SELECT,
          props: options,
          closeable: true,
          visible: true,
          ...options,
        });
      };

    @action
      confirm = async (options = {}) => {
        await this.reset(KEYS.MODAL);
        return await this.set(KEYS.MODAL, {
          id: AcUUID(),
          title: TITLES.DELETE_CONFIRM,
          tag: KEYS.CONFIRM,
          props: options,
          closeable: false,
          visible: true,
          centered: true,
          ...options,
        });
      };

    @action
      display_linked_to = async (options = {}) => {
        await this.reset(KEYS.MODAL);

        let _options = Object.assign({}, options);

        if (options.title)
          options.title = `<i class="ac-icon ac-icon--alert-circle-outline ac-icon--error"></i> ${options.title}`;
        else
          options.title = `<i class="ac-icon ac-icon--alert-circle-outline ac-icon--error"></i> ${TITLES.FAILED_TO_DELETE_RESOURCE}`;

        return await this.set(KEYS.MODAL, {
          id: AcUUID(),
          tag: KEYS.RESOURCE_LINKED_TO,
          props: options,
          closeable: true,
          visible: true,
          centered: false,
          callback: async () =>
            await this.setValue(KEYS.MODAL, KEYS.VISIBLE, false),
          ...options,
        });
      };

    @action
      setInstance = (target, value) => {
        return new Promise((resolve) => {
          if (!AcIsSet(target)) return;
          if (AcIsUndefined(this[target])) return;
          if (AcIsUndefined(value)) return;

          const initial = { ..._default[target] };

          if (AcIsSet(initial)) {
            runInAction(() => {
              const newInstance = {
                ...initial,
                ...value,
              };
              set(this[target], newInstance);
              // this[target] = newInstance;
            });
          } else {
            runInAction(() => {
              set(this[target], value);
            });
          }

          resolve();
        });
      };

    @action
      setValue = (target, property, value) => {
        return new Promise((resolve, reject) => {
          if (_delay && _target === target) clearTimeout(_delay);

          if (!AcIsSet(target)) {
            reject(`[store][ui] action.setValue: 'target' is not defined`);
            return;
          } else if (!AcIsSet(property)) {
            reject(`[store][ui] action.setValue: 'property' is not defined`);
            return;
          } else if (AcIsUndefined(this[target])) {
            reject(
              `[store][ui] action.setValue: target '${target}' is not an ui element`
            );
            return;
          } else if (AcIsUndefined(this[target][property])) {
            reject(
              `[store][ui] action.setValue: property '${property}' is not part of target ${target}`
            );
            return;
          } else if (AcIsUndefined(value)) {
            reject(`[store][ui] action.setValue: value is not defined`);
            return;
          }

          if (
            target === KEYS.MESSAGE_CENTER &&
                property === KEYS.VISIBLE &&
                value === true
          ) {
            setTimeout(() => {
              requestAnimationFrame(() => {
                runInAction(() => {
                  this.support_center.visible = false;
                });
              });
            }, 1000 / 60); // attempt to keep the fps on 60
            setTimeout(() => {
              requestAnimationFrame(() => {
                runInAction(() => {
                  this.sub_navigation.visible = false;
                });
              });
            }, 1000 / 60); // attempt to keep the fps on 60
          } else if (
            target === KEYS.SUPPORT_CENTER &&
                property === KEYS.VISIBLE &&
                value === true
          ) {
            setTimeout(() => {
              requestAnimationFrame(() => {
                runInAction(() => {
                  this.message_center.visible = false;
                });
              });
            }, 1000 / 60); // attempt to keep the fps on 60
            setTimeout(() => {
              requestAnimationFrame(() => {
                runInAction(() => {
                  this.sub_navigation.visible = false;
                });
              });
            }, 1000 / 60); // attempt to keep the fps on 60
          } else if (
            target === KEYS.SUB_NAVIGATION &&
                property === KEYS.VISIBLE &&
                value === true
          ) {
            setTimeout(() => {
              requestAnimationFrame(() => {
                runInAction(() => {
                  this.support_center.visible = false;
                });
              });
            }, 1000 / 60); // attempt to keep the fps on 60
            setTimeout(() => {
              requestAnimationFrame(() => {
                runInAction(() => {
                  this.message_center.visible = false;
                });
              });
            }, 1000 / 60); // attempt to keep the fps on 60
          }

          setTimeout(() => {
            requestAnimationFrame(() => {
              runInAction(() => {
                this[target][property] = value;
                resolve();
              });
            });
          }, 1000 / 60); // attempt to keep the fps on 60
        });
      };

    @action
      reset = (target) => {
        return new Promise((resolve, reject) => {
          if (AcIsUndefined(target)) return;
          if (AcIsUndefined(this[target])) return;

          runInAction(() => (this[target] = _default[target]));
          resolve();
        });
      };
}

export default UiStore;
